import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * In-app message notification hook — WhatsApp-style banner
 * Shows a slide-down notification when a new message arrives.
 * Displays sender name (or company name for vendors).
 * Tapping opens the conversation.
 */
export function useInAppMessageNotification() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  const navigateRef = useRef(navigate);

  // Keep refs updated without causing re-subscription
  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  useEffect(() => {
    if (!user) return;

    const channelId = `inapp-msg-notif-${user.id}`;
    const channel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `receiver_id=eq.${user.id}`,
      }, async (payload) => {
        const msg = payload.new as any;
        if (!msg || msg.sender_id === user.id) return;

        // Don't show if user is already on the chat page with this sender
        if (pathnameRef.current === `/chat/${msg.sender_id}`) return;

        // Fetch sender profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, avatar_url')
          .eq('user_id', msg.sender_id)
          .single();

        // Check if sender is a vendor/firma
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', msg.sender_id);

        const isFirma = (roles || []).some((r: any) =>
          ['firma', 'vendor', 'company'].includes(r.role)
        );

        const senderName = profile?.display_name || (isFirma ? 'Firma' : 'Kullanıcı');
        const avatarUrl = profile?.avatar_url;
        const messageText = msg.content || msg.message || '';
        const senderId = msg.sender_id;

        showMessageBanner(senderName, messageText, avatarUrl, isFirma, () => {
          navigateRef.current(`/chat/${senderId}`);
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]); // Only re-subscribe when user changes
}

/**
 * Shows a WhatsApp-style notification banner at the top of the screen
 */
function showMessageBanner(
  senderName: string,
  messageText: string,
  avatarUrl: string | null,
  isFirma: boolean,
  onTap: () => void
) {
  // Remove existing banner if any
  const existing = document.querySelector('.msg-notification-banner');
  if (existing) existing.remove();

  const truncatedMsg = messageText.length > 120 ? messageText.substring(0, 120) + '…' : messageText;

  const banner = document.createElement('div');
  banner.className = 'msg-notification-banner';
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 99999;
    padding: max(12px, env(safe-area-inset-top)) 16px 12px;
    cursor: pointer;
    animation: msgSlideDown 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: auto;
  `;

  const badgeColor = isFirma ? '#1a5c2e' : '#2563eb';
  const avatarHtml = avatarUrl
    ? `<img src="${avatarUrl}" style="width:44px;height:44px;border-radius:12px;object-fit:cover;flex-shrink:0;" />`
    : `<div style="width:44px;height:44px;border-radius:12px;background:${badgeColor};display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:18px;color:white;">${senderName.charAt(0).toUpperCase()}</div>`;

  banner.innerHTML = `
    <div style="
      background: rgba(255,255,255,0.97);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08);
      border: 1px solid rgba(0,0,0,0.06);
      padding: 12px 14px;
      display: flex;
      align-items: center;
      gap: 12px;
      max-width: 480px;
      margin: 0 auto;
    ">
      ${avatarHtml}
      <div style="flex:1;min-width:0;overflow:hidden;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;">
          <span style="font-weight:700;font-size:14px;color:#111;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${senderName}</span>
          ${isFirma ? `<span style="font-size:9px;background:${badgeColor};color:white;padding:1px 6px;border-radius:4px;font-weight:600;">Firma</span>` : ''}
          <span style="font-size:10px;color:#999;margin-left:auto;flex-shrink:0;">Şimdi</span>
        </div>
        <div style="font-size:13px;color:#555;line-height:1.35;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${truncatedMsg}</div>
      </div>
      <div style="flex-shrink:0;width:8px;height:8px;background:#22c55e;border-radius:50%;"></div>
    </div>
    <style>
      @keyframes msgSlideDown {
        from { transform: translateY(-100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes msgSlideUp {
        from { transform: translateY(0); opacity: 1; }
        to { transform: translateY(-100%); opacity: 0; }
      }
    </style>
  `;

  // Tap to open conversation
  banner.addEventListener('click', () => {
    banner.remove();
    onTap();
  });

  // Swipe up to dismiss
  let startY = 0;
  banner.addEventListener('touchstart', (e) => { startY = e.touches[0].clientY; }, { passive: true });
  banner.addEventListener('touchend', (e) => {
    const endY = e.changedTouches[0].clientY;
    if (startY - endY > 30) {
      banner.style.animation = 'msgSlideUp 0.25s ease-in forwards';
      setTimeout(() => banner.remove(), 250);
    }
  });

  document.body.appendChild(banner);

  // Play WhatsApp Klasik notification sound
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const now = ctx.currentTime;

    // First tone — C6
    const g1 = ctx.createGain();
    g1.connect(ctx.destination);
    g1.gain.setValueAtTime(0.35, now);
    g1.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
    const o1 = ctx.createOscillator();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(1047, now);
    o1.connect(g1);
    o1.start(now);
    o1.stop(now + 0.15);

    // Second tone — E6
    const g2 = ctx.createGain();
    g2.connect(ctx.destination);
    g2.gain.setValueAtTime(0.3, now + 0.12);
    g2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(1319, now + 0.12);
    o2.connect(g2);
    o2.start(now + 0.12);
    o2.stop(now + 0.5);
  } catch {}

  // Vibrate on mobile
  if (navigator.vibrate) navigator.vibrate(200);

  // Auto dismiss after 5 seconds
  setTimeout(() => {
    if (banner.parentNode) {
      banner.style.animation = 'msgSlideUp 0.25s ease-in forwards';
      setTimeout(() => banner.remove(), 250);
    }
  }, 5000);
}
